import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { listIncomingOffers, type Offer } from '@/lib/offers';
import { formatAmount } from '@/lib/format';

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function OffersIndexScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await listIncomingOffers();
      setOffers(data);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  return (
    <View style={styles.root}>
      <View style={[styles.header, { paddingTop: insets.top + spacing.lg }]}>
        <Text style={styles.title}>{t('offers.incoming_title')}</Text>
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : offers.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="mail-open-outline" size={48} color={colors.border} />
          <Text style={styles.emptyTitle}>{t('offers.incoming_empty_title')}</Text>
          <Text style={styles.emptyText}>{t('offers.incoming_empty_body')}</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
        >
          {offers.map((item) => {
            const days = daysUntil(item.expires_at);
            return (
              <Pressable
                key={item.id}
                style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
                onPress={() =>
                  router.push({ pathname: '/(app)/offers/[id]', params: { id: item.id } })
                }
                accessibilityRole="button"
              >
                <View style={styles.cardTop}>
                  <Text style={styles.cardTitle} numberOfLines={1}>{item.title}</Text>
                  {days !== null && days > 0 && (
                    <View style={[styles.expiryBadge, days <= 3 && styles.expiryBadgeUrgent]}>
                      <Ionicons
                        name="time-outline"
                        size={11}
                        color={days <= 3 ? '#F87171' : colors.textMuted}
                      />
                      <Text style={[styles.expiryText, days <= 3 && styles.expiryTextUrgent]}>
                        {days === 1 ? `1 ${t('common.day')}` : `${days} ${t('common.days')}`}
                      </Text>
                    </View>
                  )}
                </View>
                {!!item.description && (
                  <Text style={styles.cardDesc} numberOfLines={2}>{item.description}</Text>
                )}
                <View style={styles.cardMeta}>
                  {item.cost != null && (
                    <View style={styles.metaItem}>
                      <Ionicons name="cash-outline" size={13} color={colors.textMuted} />
                      <Text style={styles.metaText}>{formatAmount(item.cost, item.currency)}</Text>
                    </View>
                  )}
                  {item.delivery_days != null && (
                    <View style={styles.metaItem}>
                      <Ionicons name="calendar-outline" size={13} color={colors.textMuted} />
                      <Text style={styles.metaText}>{item.delivery_days} {item.delivery_days === 1 ? t('common.day') : t('common.days')}</Text>
                    </View>
                  )}
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.xxl,
      fontWeight: '800',
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      paddingHorizontal: spacing.xl,
    },
    emptyTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
    },
    emptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
    },
    list: { flex: 1 },
    listContent: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.xl,
      gap: spacing.sm,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
      ...shadow.card,
    },
    cardPressed: { opacity: 0.75 },
    cardTop: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    cardTitle: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    expiryBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    expiryBadgeUrgent: {},
    expiryText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    expiryTextUrgent: {
      color: '#F87171',
    },
    cardDesc: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    cardMeta: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
      marginTop: 2,
    },
    metaItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    metaText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
  });
}
